import { FC, ReactNode } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Reusable confirmation dialog (same look as the Invoice / Expense delete
// confirm). Use anywhere a destructive or important action needs an
// "are you sure?" step.
//
//   <ConfirmDialog
//     open={open}
//     title="Delete Variant Set"
//     message={<>Are you sure you want to delete <b>{name}</b>?</>}
//     warningText="This cannot be recovered once deleted."
//     loading={saving}
//     onConfirm={handleDelete}
//     onClose={() => setOpen(false)}
//   />

type Severity = "danger" | "success";

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Small line under the title. Defaults to "This action cannot be undone". */
  subtitle?: string;
  /** Main body — the question. */
  message?: ReactNode;
  /** Optional highlighted warning box below the message. */
  warningText?: ReactNode;
  /** Heading of the warning box. Defaults to "Important Warning". */
  warningTitle?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Icon shown in the header badge and confirm button. Defaults to a trash icon. */
  icon?: ReactNode;
  /** Colour theme of the dialog. "danger" (red, default) or "success" (green). */
  severity?: Severity;
  loading?: boolean;
  /** Single-button notice (e.g. an error): drops the Cancel button. */
  hideCancel?: boolean;
}

const THEME: Record<Severity, { header: string; border: string; badge: string; icon: string; btn: string; btnHover: string }> = {
  danger: { header: "#fef2f2", border: "#fecaca", badge: "#fee2e2", icon: "#dc2626", btn: "#dc2626", btnHover: "#b91c1c" },
  success: { header: "#f0fdf4", border: "#bbf7d0", badge: "#dcfce7", icon: "#16a34a", btn: "#16a34a", btnHover: "#15803d" },
};

const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  subtitle = "This action cannot be undone",
  message,
  warningText,
  warningTitle = "Important Warning",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  icon,
  severity = "danger",
  loading = false,
  hideCancel = false,
}) => {
  const t = THEME[severity];
  const headerIcon = icon ?? <DeleteIcon sx={{ color: t.icon, fontSize: 28 }} />;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <DialogTitle sx={{ backgroundColor: t.header, borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 2, py: 3, px: 3 }}>
        <Box sx={{ backgroundColor: t.badge, borderRadius: "50%", p: 1.5, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {headerIcon}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937", fontSize: "1.1rem" }}>{title}</Typography>
          {subtitle && <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>{subtitle}</Typography>}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 3, px: 3 }}>
        {message && (
          <Typography variant="body1" sx={{ color: "#1f2937", mt: 1 }}>
            {message}
          </Typography>
        )}
        {warningText && (
          <Box sx={{ mt: 2, backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 1.5, px: 2, py: 1.5, display: "flex", gap: 1.5 }}>
            <WarningAmberIcon sx={{ color: "#d97706", fontSize: 22, mt: 0.2 }} />
            <Box>
              {warningTitle && (
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#b45309" }}>{warningTitle}</Typography>
              )}
              <Typography variant="body2" sx={{ color: "#b45309" }}>{warningText}</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 3, py: 2, gap: 1.5 }}>
        {!hideCancel && (
          <Button onClick={onClose} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: "none", borderColor: "#d1d5db", color: "#374151" }}>
            {cancelLabel}
          </Button>
        )}
        <LoadingButton
          onClick={onConfirm}
          loading={loading}
          variant="contained"
          startIcon={hideCancel ? undefined : icon ?? <DeleteIcon sx={{ fontSize: 18 }} />}
          sx={{ minWidth: 140, height: 40, textTransform: "none", fontWeight: 600, backgroundColor: t.btn, "&:hover": { backgroundColor: t.btnHover } }}
        >
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
